
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Shield, Clock } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'sonner';
import FrontendLogger, { frontendLogger } from '@/components/debug/FrontendLogger';

// Import the athlete layout and dashboard components
import AthleteLayout from '@/layouts/AthleteLayout';
import AthleteDashboard from '@/pages/modules/AthleteDashboard';

// Create an isolated Supabase client for impersonation to prevent affecting parent session
const isolatedSupabase = createClient(
  'https://pvaumvowzncvrdkkvnqq.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB2YXVtdm93em5jdnJka2t2bnFxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDU5MzkwNzYsImV4cCI6MjA2MTUxNTA3Nn0.tzzUBAW0sotu9taBdFv62G3RYDAnP7C5CPluRo4Odcs',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'parent-view-athlete-session', // Isolate from main tab
    }
  }
);

interface TokenPayload {
  parent_id: string;
  athlete_id: string;
  exp: number;
  iat: number;
}

const ParentViewAthlete: React.FC = () => {
  const { athleteId } = useParams<{ athleteId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [athleteProfile, setAthleteProfile] = useState<any>(null);
  const [showLogger, setShowLogger] = useState(true);

  useEffect(() => {
    frontendLogger.log('info', '🔐 PARENT VIEW ATHLETE - Starting validation', { athleteId, hasToken: !!token });
    validateTokenAndAccess();
  }, [athleteId, token]);

  const validateTokenAndAccess = async () => {
    try {
      if (!token || !athleteId) {
        frontendLogger.log('error', '❌ Missing token or athlete ID', { token: !!token, athleteId });
        setError('Missing token or athlete ID');
        setIsValidating(false);
        return;
      }

      // Decode and validate JWT token (basic validation)
      try {
        const payload = JSON.parse(atob(token.split('.')[1])) as TokenPayload;
        frontendLogger.log('debug', 'Token payload decoded', { payload });
        
        // Check if token is expired
        if (payload.exp < Math.floor(Date.now() / 1000)) {
          frontendLogger.log('error', '❌ Token expired', { exp: payload.exp, now: Math.floor(Date.now() / 1000) });
          setError('Access token has expired');
          setIsValidating(false);
          return;
        }

        // Check if athlete ID matches
        if (payload.athlete_id !== athleteId) {
          frontendLogger.log('error', '❌ Athlete ID mismatch', { payloadAthleteId: payload.athlete_id, urlAthleteId: athleteId });
          setError('Token athlete ID mismatch');
          setIsValidating(false);
          return;
        }

        // Verify the relationship still exists and is approved using isolated client
        const { data: relationship, error: relationshipError } = await isolatedSupabase
          .from('parent_athlete_relationships')
          .select('status')
          .eq('parent_profile_id', payload.parent_id)
          .eq('athlete_profile_id', athleteId)
          .eq('status', 'approved')
          .single();

        if (relationshipError || !relationship) {
          frontendLogger.log('error', '❌ Relationship verification failed', { relationshipError, relationship });
          setError('Parent-athlete relationship not found or not approved');
          setIsValidating(false);
          return;
        }

        // Get athlete profile information using isolated client
        const { data: athlete, error: athleteError } = await isolatedSupabase
          .from('profiles')
          .select('id, name, username, sport:sport_id(name)')
          .eq('id', athleteId)
          .single();

        if (athleteError || !athlete) {
          frontendLogger.log('error', '❌ Athlete profile fetch failed', { athleteError, athlete });
          setError('Athlete profile not found');
          setIsValidating(false);
          return;
        }

        setAthleteProfile(athlete);
        
        // Set context for profile data loading
        localStorage.setItem('activeProfileId', athleteId);
        localStorage.setItem('actingAs', 'parent');
        
        frontendLogger.log('info', '✅ Token validation successful', { athleteName: athlete.name });
        setIsValid(true);
        
      } catch (tokenError) {
        frontendLogger.log('error', '❌ Token decode error', { tokenError });
        setError('Invalid token format');
        setIsValidating(false);
        return;
      }

    } catch (error) {
      console.error('Token validation error:', error);
      frontendLogger.log('error', '❌ Validation process error', { error });
      setError('Failed to validate access');
    } finally {
      setIsValidating(false);
    }
  };

  // Cleanup localStorage when component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem('activeProfileId');
      localStorage.removeItem('actingAs');
    };
  }, []);

  if (isValidating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="p-6">
            <div className="flex items-center justify-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span>Validating access...</span>
            </div>
          </CardContent>
        </Card>
        <FrontendLogger isVisible={showLogger} onClose={() => setShowLogger(false)} />
      </div>
    );
  }

  if (!isValid || error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              <span>Access Denied</span>
            </CardTitle>
            <CardDescription>
              You do not have permission to view this athlete's dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {error || 'Invalid or expired access token.'}
              </p>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Clock className="h-4 w-4" />
                <span>Access tokens expire after 15 minutes for security</span>
              </div>
            </div>
          </CardContent>
        </Card>
        <FrontendLogger isVisible={showLogger} onClose={() => setShowLogger(false)} />
      </div>
    );
  }

  // If validation passes, render the full athlete layout with security indicator
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Security indicator banner */}
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
        <div className="flex items-center justify-center space-x-2 text-sm text-blue-700">
          <Shield className="h-4 w-4" />
          <span>
            Viewing {athleteProfile?.name}'s dashboard as authorized parent
          </span>
        </div>
      </div>
      
      {/* Render the full athlete layout */}
      <div className="flex h-screen bg-gray-50">
        <div className="flex flex-col flex-1 overflow-hidden">
          <main className="flex-1 overflow-y-auto bg-gray-50 p-4 sm:p-6">
            <AthleteDashboard />
          </main>
        </div>
      </div>

      <FrontendLogger isVisible={showLogger} onClose={() => setShowLogger(false)} />
    </div>
  );
};

export default ParentViewAthlete;
